import { useSearchParams } from 'react-router-dom'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Alert, Card, CardContent, CardHeader, CardTitle } from '../components/ui'

/**
 * ErrorPage - OAuth error display
 *
 * Shows OAuth errors that cannot be redirected back to the app.
 * This happens when:
 * - Invalid client_id (can't trust redirect_uri)
 * - Invalid redirect_uri (can't redirect to it)
 * - Other security-sensitive errors
 */
export function ErrorPage() {
  const [searchParams] = useSearchParams()

  const error = searchParams.get('error') || 'unknown_error'
  const errorDescription = searchParams.get('error_description') || getDefaultDescription(error)

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Authorization Error</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="error" className="mb-4">
            <div className="font-medium">{formatError(error)}</div>
            {errorDescription && (
              <p className="mt-1">{errorDescription}</p>
            )}
          </Alert>

          <div className="mt-4 rounded-lg bg-[var(--bg-elevated)] p-4">
            <p className="text-sm text-[var(--text-secondary)]">
              If you were trying to sign in to an application, please return to that
              application and try again. If the problem persists, contact the
              application developer.
            </p>
          </div>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-sm font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
            >
              Go to u.software home
            </a>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}

function formatError(error: string): string {
  const errorNames: Record<string, string> = {
    invalid_request: 'Invalid Request',
    unauthorized_client: 'Unauthorized Client',
    access_denied: 'Access Denied',
    unsupported_response_type: 'Unsupported Response Type',
    invalid_scope: 'Invalid Scope',
    server_error: 'Server Error',
    temporarily_unavailable: 'Temporarily Unavailable',
    invalid_client: 'Invalid Client',
    invalid_redirect_uri: 'Invalid Redirect URI',
    unknown_error: 'Unknown Error',
  }
  return errorNames[error] || error.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function getDefaultDescription(error: string): string {
  const descriptions: Record<string, string> = {
    invalid_request: 'The request is missing a required parameter or is malformed.',
    unauthorized_client: 'The client is not authorized to request authorization.',
    access_denied: 'The resource owner denied the request.',
    unsupported_response_type: 'The authorization server does not support this response type.',
    invalid_scope: 'The requested scope is invalid or unknown.',
    server_error: 'The authorization server encountered an unexpected condition.',
    temporarily_unavailable: 'The authorization server is currently unavailable.',
    invalid_client: 'The client identifier provided is invalid.',
    invalid_redirect_uri: 'The redirect URI provided does not match any registered for this client.',
  }
  return descriptions[error] || 'An error occurred during authorization.'
}
